import React, { useState, useMemo, useCallback } from 'react';
import AntLocalization from '../../utils/AntLocalization';

function getDaysInMonth(year, month) {
    return new Date(year, month + 1, 0).getDate();
}

function isSameDay(a, b) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export default function MiniCalendar(props) {
    var {
        selectedDate,
        onDateSelect,
        occupancyData,
        month: monthProp,
        year: yearProp
    } = props;

    var today = new Date();
    var [viewMonth, setViewMonth] = useState(monthProp != null ? monthProp : (selectedDate || today).getMonth());
    var [viewYear, setViewYear] = useState(yearProp != null ? yearProp : (selectedDate || today).getFullYear());

    var lang = useMemo(function () {
        try { return AntLocalization.getLanguage() || 'vi'; } catch (e) { return 'vi'; }
    }, []);

    var monthNames = useMemo(function () {
        return {
            vi: ['Tháng 1', 'Tháng 2', 'Tháng 3', 'Tháng 4', 'Tháng 5', 'Tháng 6', 'Tháng 7', 'Tháng 8', 'Tháng 9', 'Tháng 10', 'Tháng 11', 'Tháng 12'],
            en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        };
    }, []);

    var dayHeaders = useMemo(function () {
        if (lang === 'en') return ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
        return ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'];
    }, [lang]);

    // Generate calendar grid
    var calendarDays = useMemo(function () {
        var days = [];
        var daysInMonth = getDaysInMonth(viewYear, viewMonth);
        var firstDay = new Date(viewYear, viewMonth, 1).getDay();
        // Adjust for Monday start
        var startOffset = firstDay === 0 ? 6 : firstDay - 1;

        // Previous month padding
        var prevMonthDays = getDaysInMonth(viewYear, viewMonth - 1);
        for (var p = startOffset - 1; p >= 0; p--) {
            days.push({
                day: prevMonthDays - p,
                date: new Date(viewYear, viewMonth - 1, prevMonthDays - p),
                currentMonth: false
            });
        }

        // Current month
        for (var d = 1; d <= daysInMonth; d++) {
            days.push({
                day: d,
                date: new Date(viewYear, viewMonth, d),
                currentMonth: true
            });
        }

        // Next month padding (fill to 42 = 6 rows)
        var remaining = 42 - days.length;
        for (var n = 1; n <= remaining; n++) {
            days.push({
                day: n,
                date: new Date(viewYear, viewMonth + 1, n),
                currentMonth: false
            });
        }

        return days;
    }, [viewYear, viewMonth]);

    var handlePrevMonth = useCallback(function () {
        setViewMonth(function (m) {
            if (m === 0) {
                setViewYear(function (y) { return y - 1; });
                return 11;
            }
            return m - 1;
        });
    }, []);

    var handleNextMonth = useCallback(function () {
        setViewMonth(function (m) {
            if (m === 11) {
                setViewYear(function (y) { return y + 1; });
                return 0;
            }
            return m + 1;
        });
    }, []);

    var names = monthNames[lang] || monthNames.vi;

    return (
        <div className="mini-calendar">
            {/* Month navigation */}
            <div className="mini-cal-header">
                <button className="mini-cal-nav" onClick={handlePrevMonth}>◀</button>
                <span className="mini-cal-title">{names[viewMonth]} {viewYear}</span>
                <button className="mini-cal-nav" onClick={handleNextMonth}>▶</button>
            </div>

            {/* Day headers */}
            <div className="mini-cal-days-header">
                {dayHeaders.map(function (dh) {
                    return <span key={dh} className="day-header">{dh}</span>;
                })}
            </div>

            {/* Day grid */}
            <div className="mini-cal-grid">
                {calendarDays.map(function (dayObj, idx) {
                    var cls = 'cal-day';
                    if (!dayObj.currentMonth) cls += ' other-month';
                    if (isSameDay(dayObj.date, today)) cls += ' today';
                    if (selectedDate && isSameDay(dayObj.date, selectedDate)) cls += ' selected';

                    var isWeekend = dayObj.date.getDay() === 0 || dayObj.date.getDay() === 6;
                    if (isWeekend) cls += ' weekend';

                    // Occupancy dot
                    var occupancy = null;
                    if (occupancyData && dayObj.currentMonth) {
                        var dateKey = dayObj.date.toISOString().split('T')[0];
                        occupancy = occupancyData[dateKey];
                    }

                    var dotCls = '';
                    if (occupancy != null) {
                        if (occupancy >= 80) dotCls = 'dot-high';
                        else if (occupancy >= 50) dotCls = 'dot-medium';
                        else if (occupancy > 0) dotCls = 'dot-low';
                    }

                    return (
                        <div
                            key={idx}
                            className={cls}
                            onClick={function () {
                                if (onDateSelect) onDateSelect(dayObj.date);
                            }}
                        >
                            <span className="cal-day-num">{dayObj.day}</span>
                            {dotCls && <span className={'cal-day-dot ' + dotCls} />}
                        </div>
                    );
                })}
            </div>

            {/* Legend */}
            {occupancyData && (
                <div className="mini-cal-legend">
                    <span className="legend-item"><span className="cal-day-dot dot-low" /> &lt;50%</span>
                    <span className="legend-item"><span className="cal-day-dot dot-medium" /> 50-80%</span>
                    <span className="legend-item"><span className="cal-day-dot dot-high" /> &gt;80%</span>
                </div>
            )}
        </div>
    );
}
